/** Small, shared text helpers so pages stop hand-rolling the same regexes. */

/** `video_editor` -> `video editor`, `raws_review` -> `raws review`. */
/** "18h left", "2d 4h left", "3h over". Null when there is no clock. */
export function remaining(dueIso?: string | null, now: number = Date.now()): { text: string; over: boolean; soon: boolean } | null {
  if (!dueIso) return null;
  const ms = new Date(dueIso).getTime() - now;
  const over = ms < 0;
  const abs = Math.abs(ms);
  const h = Math.floor(abs / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  const d = Math.floor(h / 24);
  const span = d >= 1 ? `${d}d ${h % 24}h` : h >= 1 ? `${h}h ${m}m` : `${m}m`;
  return { text: over ? `${span} over` : `${span} left`, over, soon: !over && ms < 6 * 3600000 };
}

export function humanize(s: string | null | undefined): string {
  return (s ?? '').replace(/_/g, ' ');
}

/** `video_editor` -> `Video editor`. */
export function titleize(s: string | null | undefined): string {
  const h = humanize(s);
  return h ? h.charAt(0).toUpperCase() + h.slice(1) : '';
}

/** A period shown to people: "9 May 2026 to 8 Jul 2026", never an ASCII arrow. */
export function periodLabel(start?: string | null, end?: string | null): string {
  if (!start && !end) return 'No dates yet';
  return `${start ?? 'open'} to ${end ?? 'open'}`;
}

/** Sign-ins were only recorded reliably from this date (Google sign-in used to skip it).
 *  Before it, a missing "last seen" means unknown, not "never signed in". */
export const SEEN_TRACKED_SINCE = '2026-09-15';
export function seenLabel(lastSeenAt?: string | null, createdAt?: string | null): string {
  if (lastSeenAt) return `seen ${new Date(lastSeenAt).toLocaleDateString('en-AE')}`;
  if (createdAt && createdAt.slice(0, 10) >= SEEN_TRACKED_SINCE) return 'never signed in';
  return 'no sign-in recorded yet';
}

/**
 * A server error as one readable sentence, never an object.
 *
 * A 422 from the API carries `detail` as a LIST of validation objects
 * ({type, loc, msg, input}). Rendering that straight into JSX crashes the page with
 * React error #31, which is what "Something broke" was on the taxonomy editor when a
 * new face had no id. Everything that shows an API error should come through here.
 */
export function apiError(e: unknown, fallback = 'Something went wrong'): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => {
        if (typeof d === 'string') return d;
        const o = d as { msg?: string; loc?: unknown[] };
        const where = Array.isArray(o.loc) ? o.loc.filter((x) => typeof x === 'string' && x !== 'body').join(' ') : '';
        return [where, o.msg].filter(Boolean).join(': ');
      })
      .filter(Boolean);
    if (parts.length) return parts.slice(0, 3).join(' · ');
  }
  const msg = (e as { message?: string })?.message;
  return (typeof msg === 'string' && msg) || fallback;
}

